export type Specimen = {
  accession: string;
  slug: string;
  title: string;
  scientificName: string;
  cabinet: 'kinetic' | 'molecular';
  shortDescription: string;
  method: string;
  phenomenon: string;
  interaction: string;
  palette: [string, string, string];
  preview: string;
};

export const specimens: Specimen[] = [
  {
    accession: 'PS–001',
    slug: 'orbit-choir',
    title: 'Orbit Choir',
    scientificName: 'Harmonia epicyclia',
    cabinet: 'kinetic',
    shortDescription: 'A celestial score composed from coupled circles, patient chords, and seeded dissonance.',
    method: 'Harmonic orbits',
    phenomenon: 'Nearness becoming a temporary chord',
    interaction: 'Tune voices, resonance, reach, and palette',
    palette: ['#173f3b', '#d97757', '#f0c987'],
    preview: '/previews/orbit-choir.webp',
  },
  {
    accession: 'PS–002',
    slug: 'moire-loom',
    title: 'Moiré Loom',
    scientificName: 'Textura interferens',
    cabinet: 'kinetic',
    shortDescription: 'Two families of digital thread lean into one another until an impossible textile appears.',
    method: 'Interference weaving',
    phenomenon: 'Crossing fields and traveling interference',
    interaction: 'Adjust threads, warp, crossing, and ink',
    palette: ['#1f3557', '#d97757', '#faf4e8'],
    preview: '/previews/moire-loom.webp',
  },
  {
    accession: 'PS–003',
    slug: 'recursive-rooms',
    title: 'Recursive Rooms',
    scientificName: 'Camera infinitum',
    cabinet: 'kinetic',
    shortDescription: 'An architectural organism that keeps finding smaller thresholds inside itself.',
    method: 'Recursive subdivision',
    phenomenon: 'A finite view onto an address without an end',
    interaction: 'Change depth, aperture, lean, and light',
    palette: ['#302b26', '#bc705b', '#e6bb72'],
    preview: '/previews/recursive-rooms.webp',
  },
  {
    accession: 'PS–004',
    slug: 'noise-garden',
    title: 'Noise Garden',
    scientificName: 'Viriditas stochastica',
    cabinet: 'kinetic',
    shortDescription: 'A quiet herbarium grown from invisible winds and a reproducible packet of chance.',
    method: 'Noise-guided growth',
    phenomenon: 'Local weather accumulating into form',
    interaction: 'Cultivate stems, curl, bloom, and color',
    palette: ['#31594b', '#d68b73', '#e2bd55'],
    preview: '/previews/noise-garden.webp',
  },
  {
    accession: 'PS–005',
    slug: 'endless-messenger',
    title: 'Endless Messenger',
    scientificName: 'Nuntius continuus',
    cabinet: 'molecular',
    shortDescription: 'A boundless archive of finite synthetic messages, read three letters at a time by patient clerks.',
    method: 'Seeded transcript grammar',
    phenomenon: 'Finite messages moving through a shared reading window',
    interaction: 'Browse transcripts, reading frames, and polysome traffic',
    palette: ['#214b47', '#cc6548', '#edc96e'],
    preview: '/previews/endless-messenger.webp',
  },
  {
    accession: 'PS–006',
    slug: 'folded-whisper',
    title: 'Folded Whisper',
    scientificName: 'Structura susurrans',
    cabinet: 'molecular',
    shortDescription: 'A synthetic RNA ribbon appears one letter at a time and learns where its own ink can meet.',
    method: 'Non-crossing pairing grammar',
    phenomenon: 'Local complementarity becoming stems, loops, and pauses',
    interaction: 'Step nucleotide by nucleotide; alter reach and wobble',
    palette: ['#273c55', '#a94c4c', '#d5b45b'],
    preview: '/previews/folded-whisper.webp',
  },
  {
    accession: 'PS–007',
    slug: 'vesicle-weather',
    title: 'Driven Demixing',
    scientificName: 'Coexistentia agitata',
    cabinet: 'molecular',
    shortDescription: 'A prepared binary fluid crosses a quench, grows interfaces, and negotiates a restless stationary pattern under shear.',
    method: 'Driven binary Lennard–Jones dynamics',
    phenomenon: 'A mixed fluid becoming domains, ribbons, and a fluctuating stationary window',
    interaction: 'Set the quench, compatibility, shear, and observation speed',
    palette: ['#1d5360', '#c96046', '#efe0bd'],
    preview: '/previews/vesicle-weather.webp',
  },
  {
    accession: 'PS–008',
    slug: 'confined-tides',
    title: 'Reciprocal-Space Desk',
    scientificName: 'Imago reciproca',
    cabinet: 'molecular',
    shortDescription: 'The same driven mixture is folded into wave space, where domain growth appears as moving intensity and a turning axis.',
    method: 'Composition structure-factor register',
    phenomenon: 'The real-space trajectory becoming rings, lobes, and plateau evidence',
    interaction: 'Read the same quench through reciprocal space and bounded time tapes',
    palette: ['#17272d', '#c96046', '#efe0bd'],
    preview: '/previews/confined-tides.webp',
  },
];

export const specimenHref = (slug: string, seed = 1) => `/toys/${slug}/?seed=${seed}`;
